import { Box, Grid, Typography, Button, Chip } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useMediaQuery } from '@mui/material';
import React, { useState, useEffect, useRef, useCallback } from 'react';
import moment from 'moment';
import SockJS from 'sockjs-client';
import { CompatClient, Stomp } from '@stomp/stompjs';
import Profile from '../../../Assets/profile1.png';
import { 
    getNotifications, 
    getNotificationCount, 
    updateNotificationCount 
} from '../../../Utils/apiService';
import storage from '../../../storageService';

// WebSocket configuration
const WEBSOCKET_BASE_URL = 'https://turbosend-dev-bam-api.turboverse.co';

interface INotification {
    id: string;
    title?: string;
    name?: string;
    message?: string;
    body?: string;
    timestamp: number;
    type?: "booking" | "reminder" | "review" | string;
    seenStatus?: boolean;
    isUnread?: boolean;
    imageUrl?: string;
    img?: string;
    topic?: string;
    templateId?: string;
    userId?: string;
    analytics_label?: string;
    url?: string;
    timing?: string;
    status?: string;
    category?: string;
}

interface IGroupedNotifications {
    today: INotification[];
    yesterday: INotification[];
    thisWeek: INotification[];
    earlier: INotification[];
}

interface AuthUser {
    applicantId: string;
}

interface NotificationModalProps {
    handleClose?: () => void;
    fromNotification?: boolean;
    user?: AuthUser;
    onNotificationCountUpdate?: (count: number) => void;
    refreshNotificationCount?: () => Promise<void>;
}

const style = {
  width: { xs: 350, sm: 400, md: 450 },
  bgcolor: 'background.paper',
  p: 3,
  borderRadius: '12px',
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
  maxHeight: '80vh',
  overflow: 'auto',
};

const NotificationModal: React.FC<NotificationModalProps> = ({ 
    handleClose, 
    fromNotification = false,
    user,
    onNotificationCountUpdate,
    refreshNotificationCount
}) => {
  const theme = useTheme();
  const isMobilescreen = useMediaQuery(theme.breakpoints.down("sm"));
  const [perpage, setPerPage] = useState(20);
  const [curPage, setCurPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [isFetchingMore, setIsFetchingMore] = useState(false);
  const [groupedNotifications, setGroupedNotifications] = useState<IGroupedNotifications>({
    today: [],
    yesterday: [],
    thisWeek: [],
    earlier: [],
  });
  const [tabIndex, setTabIndex] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const [totalPages, setTotalPages] = useState(0);
  const [notificationsList, setNotificationsList] = useState<INotification[]>([]);
  const [unreadNotifications, setUnreadNotifications] = useState<any[]>([]);
  const [userid, setUserid] = useState<string | null>(null);
  const [stompClient, setStompClient] = useState<CompatClient | null>(null);
  const [connectionStatus, setConnectionStatus] = useState<'disconnected' | 'connecting' | 'connected' | 'error'>('disconnected');
  const subscriptionRef = useRef<any>(null);
  const isWebSocketConnecting = useRef(false);
  const reconnectTimeoutRef = useRef<number | null>(null);
  const autoMarkedRef = useRef<Set<string>>(new Set());
  const scrollPositionRef = useRef<number>(0);
  const debounceTimeoutRef = useRef<number | null>(null);

  // Get userId from storage on component mount
  useEffect(() => {
    const getUserId = async () => {
      try {
        // First try to get from userDetails.ContactId in localStorage
        const userDetailsStr = localStorage.getItem('userDetails');
        let storedUserId = null;
        
        if (userDetailsStr) {
          try {
            const userDetails = JSON.parse(userDetailsStr);
            storedUserId = userDetails?.ContactId;
          } catch (parseError) {
            console.error('Error parsing userDetails from localStorage:', parseError);
          }
        }
        
        setUserid(storedUserId);
      } catch (error) {
        console.error('Error getting userId from storage:', error);
      }
    };
    getUserId();
  }, []);

  // WebSocket connection function
  const connectWebSocket = useCallback((): Promise<CompatClient> => {
    return new Promise(async (resolve, reject) => {
      try {
        setConnectionStatus('connecting');
        
        const socket = new SockJS(`${WEBSOCKET_BASE_URL}/inapp-websocket`);
        const client = Stomp.over(socket);

        client.debug = (str) => console.log("STOMP from notification:", str);
        client.heartbeat.outgoing = 10000;
        client.heartbeat.incoming = 10000;
        client.reconnectDelay = 5000;

        const turbosendToken = await storage.get('turbosendToken') || 
                              JSON.parse(localStorage.getItem('turbosendToken') || '{}');
        const idToken = turbosendToken?.idToken || localStorage.getItem('access-token') || '';
        
        if (!idToken) {
          setConnectionStatus('error');
          reject(new Error('No authentication token available'));
          return;
        }
        
        client.connect(
          { 
            Authorization: `Bearer ${idToken}`,
            'client-id': `notifications-${userid || 'unknown'}`,
            'heart-beat': '10000,10000'
          },
          () => {
            console.log("WebSocket Connected successfully");
            setConnectionStatus('connected');
            setStompClient(client);
            resolve(client);
          },
          (error: any) => {
            console.error("WebSocket Connection Error:", error);
            setConnectionStatus('error');
            setStompClient(null);
            
            if (reconnectTimeoutRef.current) {
              clearTimeout(reconnectTimeoutRef.current);
            }
            reconnectTimeoutRef.current = window.setTimeout(() => {
              if (userid) {
                console.log("Attempting to reconnect WebSocket...");
                subscribeToNotificationSocket();
              }
            }, 5000);
            
            reject(error);
          }
        );
      } catch (storageError) {
        console.error("Error getting token from storage:", storageError);
        setConnectionStatus('error');
        reject(storageError);
      }
    });
  }, [userid]);

  // WebSocket subscription function
  const subscribeToNotificationSocket = useCallback(async () => {
    if (!userid || isWebSocketConnecting.current) return;

    try {
      isWebSocketConnecting.current = true;
      console.log("Setting up WebSocket subscription for user:", userid);
      
      if (subscriptionRef.current) {
        try {
          subscriptionRef.current.unsubscribe();
        } catch (error) {
          console.warn("Error unsubscribing from previous connection:", error);
        }
        subscriptionRef.current = null;
      }
      
      if (stompClient?.connected) {
        try {
          stompClient.disconnect(() =>
            console.log("Previous WebSocket connection closed")
          );
        } catch (error) {
          console.warn("Error disconnecting previous client:", error);
        }
      }

      const client = await connectWebSocket();
      
      await new Promise(resolve => setTimeout(resolve, 500));
      
      if (client.connected) {
        subscriptionRef.current = client.subscribe(
          `/topic/inAppNotification/${userid}`,
          (notification) => {
            try {
              const message = JSON.parse(notification.body);
              console.log("Received WebSocket notification:", message);
              
              const newNotification: INotification = {
                id: message.id || `ws-${Date.now()}`,
                title: message.title || '',
                name: message.name || message.title || '',
                message: message.message || message.body || '',
                body: message.body || '',
                timestamp: message.timestamp || Date.now(),
                type: message.topic || 'notification',
                seenStatus: message.seenStatus === true,
                isUnread: message.seenStatus !== true,
                imageUrl: message.imageUrl,
                img: message.imageUrl || Profile,
                topic: message.topic,
                templateId: message.templateId,
                userId: message.userId,
                analytics_label: message.analytics_label,
                url: message.url,
                timing: moment(message.timestamp || Date.now()).fromNow(),
                category: 'today'
              };
              
              setNotificationsList((prev) => {
                const updatedList = [newNotification, ...prev];
                setGroupedNotifications(groupNotificationsByDate(updatedList));
                return updatedList;
              });

              if (!newNotification.seenStatus) {
                setUnreadNotifications((prev) => {
                  const newUnreadList = [newNotification.id, ...prev];
                  
                  if (onNotificationCountUpdate) {
                    onNotificationCountUpdate(newUnreadList.length);
                  }
                  
                  return newUnreadList;
                });
              }
            } catch (parseError) {
              console.error("Error parsing WebSocket notification:", parseError);
            }
          },
          {
            'ack': 'client-individual',
            'id': `notifications-sub-${userid}`
          }
        );
        console.log("WebSocket subscription set up successfully");
      } else {
        throw new Error("Client not connected after connection attempt");
      }
    } catch (error) {
      console.error("Error setting up WebSocket subscription:", error);
      
      setTimeout(() => {
        if (userid && !stompClient?.connected) {
          console.log("Retrying WebSocket connection...");
          subscribeToNotificationSocket();
        }
      }, 10000);
    } finally {
      isWebSocketConnecting.current = false;
    }
  }, [userid, connectWebSocket, onNotificationCountUpdate, stompClient]);

  // Cleanup WebSocket on unmount
  useEffect(() => {
    return () => {
      try {
        if (reconnectTimeoutRef.current) {
          clearTimeout(reconnectTimeoutRef.current);
          reconnectTimeoutRef.current = null;
        }
        if (subscriptionRef.current) {
          subscriptionRef.current.unsubscribe();
          subscriptionRef.current = null;
        }
        if (stompClient?.connected) {
          stompClient.disconnect(() => {
            console.log("WebSocket disconnected on component unmount");
          });
        }
        setConnectionStatus('disconnected');
      } catch (error) {
        console.warn("Error during WebSocket cleanup:", error);
      }
      isWebSocketConnecting.current = false;
    };
  }, [stompClient]);

  // Setup WebSocket subscription when userid is available
  useEffect(() => {
    if (userid && !stompClient) {
      subscribeToNotificationSocket();
    }
  }, [userid]);

  // Helper functions
  const groupNotificationsByDate = (notificationsList: INotification[]) => {
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(today.getDate() - 1);
    const thisWeekStart = new Date(today);
    thisWeekStart.setDate(today.getDate() - today.getDay());
    
    const groupedNotifications = {
      today: [] as INotification[],
      yesterday: [] as INotification[],
      thisWeek: [] as INotification[],
      earlier: [] as INotification[],
    };
    
    notificationsList.forEach((notification) => {
      const notificationDate = new Date(notification.timestamp);
      if (notificationDate.toDateString() === today.toDateString()) {
        groupedNotifications.today.push(notification);
      } else if (notificationDate.toDateString() === yesterday.toDateString()) {
        groupedNotifications.yesterday.push(notification);
      } else if (notificationDate >= thisWeekStart) {
        groupedNotifications.thisWeek.push(notification);
      } else {
        groupedNotifications.earlier.push(notification);
      }
    });
    return groupedNotifications;
  };

  const formatTime = (timestamp: number) => {
    return moment(timestamp).format("hh:mm a");
  };

  const formatTitle = (title: any) => {
    return title
      .replace(/_/g, " ")
      .replace(/\b\w/g, (char: any) => char.toUpperCase());
  };

  // Mark first N unread notifications as read
  const markFirstNUnreadAsRead = useCallback(async (n: number) => {
    const unread = notificationsList
      .filter(noti => !noti.seenStatus && !autoMarkedRef.current.has(noti.id))
      .slice(0, n);

    if (unread.length === 0) return;

    const markPayload = unread.map(noti => ({ [noti.id]: true }));
    try {
      await updateNotificationCount(markPayload);
      setUnreadNotifications(prev =>
        prev.filter(id => !unread.some(noti => noti.id === id))
      );
      unread.forEach(noti => autoMarkedRef.current.add(noti.id));
      if (onNotificationCountUpdate) {
        onNotificationCountUpdate(
          unreadNotifications.length - unread.length
        );
      }
      if (refreshNotificationCount) {
        refreshNotificationCount();
      }
    } catch (err) {
      console.error("Error marking notifications as read:", err);
    }
  }, [notificationsList, unreadNotifications, onNotificationCountUpdate]);

  // Fetch notifications from API
  const fetchData = async (page = curPage) => {
    try {
      if (isFetchingMore) return;
      const tabType = tabIndex === 0 ? "alert" : "notification";
      const container = document.getElementById("scrollable-container");
      const currentScrollTop = container ? container.scrollTop : 0;
      
      const res = await getNotifications(page, perpage, userid, tabType);
      console.log("getNotifications response:", res);

      if (res?.data.status === "success") {
        setTotalPages(res?.data.totalPages || 0);

        const notificationsObj = res?.data?.data?.[0]?.notifications || {};
        console.log("Extracted notifications object:", notificationsObj);
        
        const notificationsArray = Object.entries(notificationsObj).map(
          ([id, notification]: [string, any]) => ({
            id,
            ...notification,
            seenStatus: notification.seenStatus === true,
            isUnread: notification.seenStatus !== true,
            message: notification.message || notification.body || '',
            name: notification.name || notification.title || '',
            type: notification.topic || 'notification',
            img: notification.imageUrl || Profile,
            timing: moment(notification.timestamp).fromNow(),
            category: 'today' // This would need proper categorization logic
          })
        );

        console.log("Processed notifications array:", notificationsArray);

        setNotificationsList((prevList) => {
          const updatedList = [...prevList, ...notificationsArray];
          setGroupedNotifications(groupNotificationsByDate(updatedList));
          return updatedList;
        });

        const newUnreadIds = notificationsArray
          .filter((notif) => !notif.seenStatus)
          .map((notif) => notif.id);

        setUnreadNotifications((prevUnread) => [
          ...new Set([...prevUnread, ...newUnreadIds]),
        ]);

        requestAnimationFrame(() => {
          if (container) {
            container.scrollTop = currentScrollTop;
          }
        });
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
      setIsFetchingMore(false);
    }
  };

  // Initial load when component mounts and tab changes
  useEffect(() => {
    if (userid) {
      console.log("Component mounted or tab changed to:", tabIndex);

      setLoading(true);
      setNotificationsList([]);
      setCurPage(1);
      fetchData(1);

      autoMarkedRef.current = new Set();
    }
  }, [tabIndex, userid]);

  useEffect(() => {
    if (curPage > 1) {
      setIsFetchingMore(true);
      fetchData();
    }
  }, [curPage]);

  // Mark all notifications as seen
  const markAllAsSeen = async () => {
    try {
      setLoading(true);

      let allUnreadNotificationIds: any = [];
      let currentPage = 1;
      let totalPages = 1;

      while (currentPage <= totalPages) {
        const res = await getNotificationCount(currentPage, perpage, userid);
        console.log("Notification count on page", currentPage, ":", res);

        if (res && res.data && res.data.data.length > 0) {
          const notificationsObj = res.data.data[0]?.notifications || res.data.data[0] || {};

          const unreadNotificationKeys = Object.keys(notificationsObj).filter(
            (key) => notificationsObj[key].seenStatus !== true
          );

          allUnreadNotificationIds = [
            ...allUnreadNotificationIds,
            ...unreadNotificationKeys,
          ];
        }

        totalPages = res.data.totalPages || 1;
        currentPage++;
      }

      if (allUnreadNotificationIds.length === 0) {
        console.log("No unread notifications.");
        return;
      }

      console.log(`Total unread: ${allUnreadNotificationIds.length}`);

      const BATCH_SIZE = 200;
      const batches = [];

      for (let i = 0; i < allUnreadNotificationIds.length; i += BATCH_SIZE) {
        const batch = allUnreadNotificationIds
          .slice(i, i + BATCH_SIZE)
          .map((notificationId: any) => ({
            [notificationId]: true,
          }));

        batches.push(updateNotificationCount(batch));
      }

      await Promise.all(batches);
      console.log("All notifications marked as read.");

      setUnreadNotifications([]);

      if (onNotificationCountUpdate) {
        onNotificationCountUpdate(0);
      }
      if (refreshNotificationCount) {
        refreshNotificationCount();
      }
    } catch (error) {
      console.error("Error marking notifications as read:", error);
    } finally {
      setLoading(false);
    }
  };

  // Mark individual notification as read
  const markIndividualAsRead = async (notificationId?: string) => {
    try {
      if (notificationId) {
        const result = [{ [notificationId]: true }];
        await updateNotificationCount(result);
        
        setUnreadNotifications((prevIds) => {
          const newIds = prevIds.filter((id) => id !== notificationId);
          if (onNotificationCountUpdate) {
            onNotificationCountUpdate(newIds.length);
          }
          if (refreshNotificationCount) {
            refreshNotificationCount();
          }
          return newIds;
        });

        // Update the notification in the list
        setNotificationsList(prev => 
          prev.map(notif => 
            notif.id === notificationId 
              ? { ...notif, seenStatus: true, isUnread: false }
              : notif
          )
        );
      }
    } catch (error) {
      console.error("Error marking seen:", error);
    }
  };

  // Handle scroll for pagination
  const handleScroll = useCallback(
    (event: React.UIEvent<HTMLDivElement>) => {
      const target = event.target as HTMLDivElement;

      scrollPositionRef.current = target.scrollTop;
      const { scrollHeight, scrollTop, clientHeight } = target;

      const isBottomReached =
        Math.ceil(scrollTop + clientHeight) >= scrollHeight - 1;

      if (isBottomReached && !loading && curPage < totalPages) {
        if (debounceTimeoutRef.current) {
          clearTimeout(debounceTimeoutRef.current);
        }

        debounceTimeoutRef.current = window.setTimeout(() => {
          setCurPage((prevPage) => prevPage + 1);
        }, 300);
      }
      
      if (tabIndex === 1) {
        markFirstNUnreadAsRead(5);
      }
    },
    [loading, curPage, totalPages, markFirstNUnreadAsRead, tabIndex]
  );

  useEffect(() => {
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, []);

  // Handle tab change
  const handleTabChange = (newValue: number) => {
    setTabIndex(newValue);
  };

  // Convert tab index to string for backward compatibility
  const activeTab = tabIndex === 0 ? 'All' : tabIndex === 1 ? 'Unread' : 'Read';

  // Filter notifications based on tab
  const filteredNotifications = React.useMemo(() => {
    if (tabIndex === 1) {
      return notificationsList.filter(n => n.isUnread || !n.seenStatus);
    } else if (tabIndex === 2) {
      return notificationsList.filter(n => !n.isUnread && n.seenStatus);
    }
    return notificationsList;
  }, [tabIndex, notificationsList]);

  const groupedFilteredNotifications = React.useMemo(
    () => groupNotificationsByDate(filteredNotifications),
    [filteredNotifications]
  );

  const getStatusChipStyle = (status: string) => {
    switch (status) {
      case 'Approved':
        return {
          backgroundColor: '#E8F5E8',
          color: '#2E7D32',
          fontSize: '10px',
          height: '20px',
          fontWeight: 500
        };
      case 'Denied':
        return {
          backgroundColor: '#FFEBEE',
          color: '#D32F2F',
          fontSize: '10px',
          height: '20px',
          fontWeight: 500
        };
      case 'In review':
        return {
          backgroundColor: '#FFF3E0',
          color: '#F57C00',
          fontSize: '10px',
          height: '20px',
          fontWeight: 500
        };
      default:
        return {
          backgroundColor: '#E3F2FD',
          color: '#1976D2',
          fontSize: '10px',
          height: '20px',
          fontWeight: 500
        };
    }
  };

  // Show empty state if no notifications
  if (notificationsList.length === 0 && !loading) {
    return (
      <div>
        <Box sx={style}>
          {/* Header */}
          <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
            <Grid item xs={6}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography sx={{ fontSize: '16px', fontWeight: 600, color: '#1a1a1a' }}>
                  Notification
                </Typography>
                {/* Connection Status Indicator */}
                {connectionStatus === 'connected' && (
                  <Box sx={{ 
                    width: 8, 
                    height: 8, 
                    borderRadius: '50%', 
                    bgcolor: '#4CAF50',
                    '@keyframes pulse': {
                      '0%': { opacity: 1 },
                      '50%': { opacity: 0.3 },
                      '100%': { opacity: 1 }
                    },
                    animation: 'pulse 2s infinite'
                  }} />
                )}
                {connectionStatus === 'connecting' && (
                  <Box sx={{ 
                    width: 8, 
                    height: 8, 
                    borderRadius: '50%', 
                    bgcolor: '#FF9800',
                    animation: 'pulse 1s infinite'
                  }} />
                )}
                {connectionStatus === 'error' && (
                  <Box sx={{ 
                    width: 8, 
                    height: 8, 
                    borderRadius: '50%', 
                    bgcolor: '#F44336'
                  }} />
                )}
              </Box>
            </Grid>
            <Grid item xs={6} sx={{ display: 'flex', justifyContent: 'end' }}>
              <Typography 
                sx={{ 
                  color: '#366EFF', 
                  cursor: 'pointer', 
                  fontWeight: 500, 
                  fontSize: '12px',
                  '&:hover': { textDecoration: 'underline' }
                }} 
                onClick={markAllAsSeen}
              >
                Mark all as read
              </Typography>
            </Grid>
          </Grid>

          {/* Tabs */}
          <Grid container spacing={1} sx={{ mb: 2 }}>
            {['All', 'Unread', 'Read'].map((tab, index) => (
              <Grid item xs={4} key={tab}>
                <Button
                  fullWidth
                  onClick={() => handleTabChange(index)}
                  sx={{
                    height: '32px',
                    backgroundColor: tabIndex === index ? '#366EFF' : '#F5F7FF',
                    color: tabIndex === index ? 'white' : '#366EFF',
                    fontSize: '12px',
                    fontWeight: 500,
                    textTransform: 'none',
                    borderRadius: '6px',
                    '&:hover': {
                      backgroundColor: tabIndex === index ? '#366EFF' : '#E8EFFF',
                    },
                  }}
                >
                  {tab}
                </Button>
              </Grid>
            ))}
          </Grid>

          {/* Empty State */}
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography sx={{ fontSize: '14px', color: '#999' }}>
              {loading ? "Loading notifications..." : "No notifications found"}
            </Typography>
          </Box>
        </Box>
      </div>
    );
  }

  return (
    <div>
      <Box sx={style}>
        {/* Header */}
        <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
          <Grid item xs={6}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography sx={{ fontSize: '16px', fontWeight: 600, color: '#1a1a1a' }}>
                Notification
              </Typography>
              {/* Connection Status Indicator */}
              {connectionStatus === 'connected' && (
                <Box sx={{ 
                  width: 8, 
                  height: 8, 
                  borderRadius: '50%', 
                  bgcolor: '#4CAF50',
                  '@keyframes pulse': {
                    '0%': { opacity: 1 },
                    '50%': { opacity: 0.3 },
                    '100%': { opacity: 1 }
                  },
                  animation: 'pulse 2s infinite'
                }} />
              )}
              {connectionStatus === 'connecting' && (
                <Box sx={{ 
                  width: 8, 
                  height: 8, 
                  borderRadius: '50%', 
                  bgcolor: '#FF9800',
                  animation: 'pulse 1s infinite'
                }} />
              )}
              {connectionStatus === 'error' && (
                <Box sx={{ 
                  width: 8, 
                  height: 8, 
                  borderRadius: '50%', 
                  bgcolor: '#F44336'
                }} />
              )}
            </Box>
          </Grid>
          <Grid item xs={6} sx={{ display: 'flex', justifyContent: 'end' }}>
            <Typography 
              sx={{ 
                color: '#366EFF', 
                cursor: 'pointer', 
                fontWeight: 500, 
                fontSize: '12px',
                '&:hover': { textDecoration: 'underline' }
              }} 
              onClick={markAllAsSeen}
            >
              Mark all as read
            </Typography>
          </Grid>
        </Grid>

        {/* Tabs */}
        <Grid container spacing={1} sx={{ mb: 2 }}>
          {['All', 'Unread', 'Read'].map((tab, index) => (
            <Grid item xs={4} key={tab}>
              <Button
                fullWidth
                onClick={() => handleTabChange(index)}
                sx={{
                  height: '32px',
                  backgroundColor: tabIndex === index ? '#366EFF' : '#F5F7FF',
                  color: tabIndex === index ? 'white' : '#366EFF',
                  fontSize: '12px',
                  fontWeight: 500,
                  textTransform: 'none',
                  borderRadius: '6px',
                  '&:hover': {
                    backgroundColor: tabIndex === index ? '#366EFF' : '#E8EFFF',
                  },
                }}
              >
                {tab}
              </Button>
            </Grid>
          ))}
        </Grid>

        {/* Scrollable Notification List */}
        <Box 
          id="scrollable-container"
          sx={{ 
            maxHeight: 600, 
            overflowY: "auto" 
          }}
          onScroll={handleScroll}
        >
          {/* Today Section */}
          {groupedFilteredNotifications.today.length > 0 && (
            <>
              <Typography
                sx={{
                  fontSize: '11px',
                  fontWeight: 400,
                  textAlign: 'center',
                  color: '#666',
                  mb: 2,
                  mt: 1
                }}
              >
                Today
              </Typography>

              {groupedFilteredNotifications.today.map((notification, index) => (
                <Box key={notification.id} sx={{ mb: index < groupedFilteredNotifications.today.length - 1 ? 2 : 0 }}>
                  <Grid container spacing={2} alignItems="flex-start">
                    <Grid item sx={{ position: 'relative' }}>
                      <Box
                        sx={{
                          width: '40px',
                          height: '40px',
                          borderRadius: '50%',
                          overflow: 'hidden',
                          border: '1px solid #E0E0E0'
                        }}
                      >
                        <img 
                          src={notification.img || Profile} 
                          alt={notification.name || notification.title || ''}
                          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        />
                      </Box>
                      {(notification.isUnread || !notification.seenStatus) && (
                        <Box
                          sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '8px',
                            height: '8px',
                            backgroundColor: '#4CAF50',
                            borderRadius: '50%',
                            border: '2px solid white'
                          }}
                        />
                      )}
                    </Grid>
                    
                    <Grid item xs>
                      <Box 
                        sx={{ 
                          display: 'flex', 
                          justifyContent: 'space-between', 
                          alignItems: 'flex-start',
                          cursor: 'pointer'
                        }}
                        onClick={() => markIndividualAsRead(notification.id)}
                      >
                        <Box sx={{ flex: 1, pr: 1 }}>
                          <Typography sx={{ 
                            fontWeight: 600, 
                            fontSize: '12px', 
                            color: '#1a1a1a',
                            mb: 0.5
                          }}>
                            <span style={{ color: '#366EFF' }}>{notification.name || notification.title}</span> {notification.message}
                          </Typography>
                          {notification.status && (
                            <Chip
                              label={notification.status}
                              size="small"
                              sx={getStatusChipStyle(notification.status)}
                            />
                          )}
                        </Box>
                        <Typography sx={{ 
                          fontSize: '10px', 
                          color: '#999',
                          whiteSpace: 'nowrap',
                          ml: 1
                        }}>
                          {notification.timing || formatTime(notification.timestamp)}
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                  
                  {index < groupedFilteredNotifications.today.length - 1 && (
                    <Box sx={{ 
                      height: '1px', 
                      backgroundColor: '#F0F0F0', 
                      mt: 2 
                    }} />
                  )}
                </Box>
              ))}
            </>
          )}

          {/* Yesterday Section */}
          {groupedFilteredNotifications.yesterday.length > 0 && (
            <>
              <Typography
                sx={{
                  fontSize: '11px',
                  fontWeight: 400,
                  textAlign: 'center',
                  color: '#666',
                  mb: 2,
                  mt: groupedFilteredNotifications.today.length > 0 ? 3 : 1
                }}
              >
                Yesterday
              </Typography>

              {groupedFilteredNotifications.yesterday.map((notification, index) => (
                <Box key={notification.id} sx={{ mb: index < groupedFilteredNotifications.yesterday.length - 1 ? 2 : 0 }}>
                  <Grid container spacing={2} alignItems="flex-start">
                    <Grid item>
                      <Box
                        sx={{
                          width: '40px',
                          height: '40px',
                          borderRadius: '50%',
                          overflow: 'hidden',
                          border: '1px solid #E0E0E0'
                        }}
                      >
                        <img 
                          src={notification.img || Profile} 
                          alt={notification.name || notification.title || ''}
                          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        />
                      </Box>
                    </Grid>
                    
                    <Grid item xs>
                      <Box 
                        sx={{ 
                          display: 'flex', 
                          justifyContent: 'space-between', 
                          alignItems: 'flex-start',
                          cursor: 'pointer'
                        }}
                        onClick={() => markIndividualAsRead(notification.id)}
                      >
                        <Box sx={{ flex: 1, pr: 1 }}>
                          <Typography sx={{ 
                            fontWeight: 600, 
                            fontSize: '12px', 
                            color: '#366EFF',
                            mb: 0.5
                          }}>
                            {notification.name || notification.title}
                          </Typography>
                          <Typography sx={{ 
                            fontSize: '11px', 
                            color: '#666',
                            mb: 1
                          }}>
                            {notification.message}
                          </Typography>
                          {notification.status && (
                            <Chip
                              label={notification.status}
                              size="small"
                              sx={getStatusChipStyle(notification.status)}
                            />
                          )}
                        </Box>
                        <Typography sx={{ 
                          fontSize: '10px', 
                          color: '#999',
                          whiteSpace: 'nowrap',
                          ml: 1
                        }}>
                          {notification.timing || formatTime(notification.timestamp)}
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                  
                  {index < groupedFilteredNotifications.yesterday.length - 1 && (
                    <Box sx={{ 
                      height: '1px', 
                      backgroundColor: '#F0F0F0', 
                      mt: 2 
                    }} />
                  )}
                </Box>
              ))}
            </>
          )}

          {/* This Week Section */}
          {groupedFilteredNotifications.thisWeek.length > 0 && (
            <>
              <Typography
                sx={{
                  fontSize: '11px',
                  fontWeight: 400,
                  textAlign: 'center',
                  color: '#666',
                  mb: 2,
                  mt: (groupedFilteredNotifications.today.length > 0 || groupedFilteredNotifications.yesterday.length > 0) ? 3 : 1
                }}
              >
                This Week
              </Typography>

              {groupedFilteredNotifications.thisWeek.map((notification, index) => (
                <Box key={notification.id} sx={{ mb: index < groupedFilteredNotifications.thisWeek.length - 1 ? 2 : 0 }}>
                  <Grid container spacing={2} alignItems="flex-start">
                    <Grid item>
                      <Box
                        sx={{
                          width: '40px',
                          height: '40px',
                          borderRadius: '50%',
                          overflow: 'hidden',
                          border: '1px solid #E0E0E0'
                        }}
                      >
                        <img 
                          src={notification.img || Profile} 
                          alt={notification.name || notification.title || ''}
                          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        />
                      </Box>
                    </Grid>
                    
                    <Grid item xs>
                      <Box 
                        sx={{ 
                          display: 'flex', 
                          justifyContent: 'space-between', 
                          alignItems: 'flex-start',
                          cursor: 'pointer'
                        }}
                        onClick={() => markIndividualAsRead(notification.id)}
                      >
                        <Box sx={{ flex: 1, pr: 1 }}>
                          <Typography sx={{ 
                            fontWeight: 600, 
                            fontSize: '12px', 
                            color: '#366EFF',
                            mb: 0.5
                          }}>
                            {notification.name || notification.title}
                          </Typography>
                          <Typography sx={{ 
                            fontSize: '11px', 
                            color: '#666',
                            mb: 1
                          }}>
                            {notification.message}
                          </Typography>
                          {notification.status && (
                            <Chip
                              label={notification.status}
                              size="small"
                              sx={getStatusChipStyle(notification.status)}
                            />
                          )}
                        </Box>
                        <Typography sx={{ 
                          fontSize: '10px', 
                          color: '#999',
                          whiteSpace: 'nowrap',
                          ml: 1
                        }}>
                          {notification.timing || formatTime(notification.timestamp)}
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                  
                  {index < groupedFilteredNotifications.thisWeek.length - 1 && (
                    <Box sx={{ 
                      height: '1px', 
                      backgroundColor: '#F0F0F0', 
                      mt: 2 
                    }} />
                  )}
                </Box>
              ))}
            </>
          )}

          {/* Earlier Section */}
          {groupedFilteredNotifications.earlier.length > 0 && (
            <>
              <Typography
                sx={{
                  fontSize: '11px',
                  fontWeight: 400,
                  textAlign: 'center',
                  color: '#666',
                  mb: 2,
                  mt: 3
                }}
              >
                Earlier
              </Typography>

              {groupedFilteredNotifications.earlier.map((notification, index) => (
                <Box key={notification.id} sx={{ mb: index < groupedFilteredNotifications.earlier.length - 1 ? 2 : 0 }}>
                  <Grid container spacing={2} alignItems="flex-start">
                    <Grid item>
                      <Box
                        sx={{
                          width: '40px',
                          height: '40px',
                          borderRadius: '50%',
                          overflow: 'hidden',
                          border: '1px solid #E0E0E0'
                        }}
                      >
                        <img 
                          src={notification.img || Profile} 
                          alt={notification.name || notification.title || ''}
                          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        />
                      </Box>
                    </Grid>
                    
                    <Grid item xs>
                      <Box 
                        sx={{ 
                          display: 'flex', 
                          justifyContent: 'space-between', 
                          alignItems: 'flex-start',
                          cursor: 'pointer'
                        }}
                        onClick={() => markIndividualAsRead(notification.id)}
                      >
                        <Box sx={{ flex: 1, pr: 1 }}>
                          <Typography sx={{ 
                            fontWeight: 600, 
                            fontSize: '12px', 
                            color: '#366EFF',
                            mb: 0.5
                          }}>
                            {notification.name || notification.title}
                          </Typography>
                          <Typography sx={{ 
                            fontSize: '11px', 
                            color: '#666',
                            mb: 1
                          }}>
                            {notification.message}
                          </Typography>
                          {notification.status && (
                            <Chip
                              label={notification.status}
                              size="small"
                              sx={getStatusChipStyle(notification.status)}
                            />
                          )}
                        </Box>
                        <Typography sx={{ 
                          fontSize: '10px', 
                          color: '#999',
                          whiteSpace: 'nowrap',
                          ml: 1
                        }}>
                          {notification.timing || formatTime(notification.timestamp)}
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                  
                  {index < groupedFilteredNotifications.earlier.length - 1 && (
                    <Box sx={{ 
                      height: '1px', 
                      backgroundColor: '#F0F0F0', 
                      mt: 2 
                    }} />
                  )}
                </Box>
              ))}
            </>
          )}

          {/* Loading indicator for pagination */}
          {isFetchingMore && (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
              <Typography sx={{ fontSize: "12px", color: "#8E8E8E" }}>
                Loading more notifications...
              </Typography>
            </Box>
          )}

          {/* No notifications message */}
          {groupedFilteredNotifications.today.length === 0 && 
           groupedFilteredNotifications.yesterday.length === 0 && 
           groupedFilteredNotifications.thisWeek.length === 0 && 
           groupedFilteredNotifications.earlier.length === 0 && 
           !loading && (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography sx={{ fontSize: '14px', color: '#999' }}>
                No notifications found
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
    </div>
  );
};

export default NotificationModal;
